import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { ActionReducerMapBuilder } from "@reduxjs/toolkit/src/mapBuilders";
import { NoInfer } from "@reduxjs/toolkit/src/tsHelpers";
import { setUser } from "../../common/user";
import { wlCreatorService } from "../../../services/admin/wlCreator.service";
import {
	setGeneralProgressHide,
	setGeneralProgressShow,
  setGeneralSnackbarState,
} from "../../common/ui";


export type payloadList = {
	page: string | number | undefined;
	perPage: string | number | undefined;
	filter: {
		nameServerStatus?: string,
		createdByIds?: [],
	  trackerIds?: [],
	  search?: string,
  }
};

export type payloadFilterCreator = {
  page: string | number | undefined;
  search: string | null;
  perPage: string | number | undefined;
}

export interface IFilterCreatorData {
  list: {
    id: string | number;
    value: string;
  }[];
  totalPages: number;
}

export type columnsItem = {
  name: string;
  type: string;
  sort: boolean;
  match?: boolean;
};

export type listItem = {
  [key: string]: any;
};

export interface ITableData {
  list: listItem[];
  columns: columnsItem[];
	totalEntries: number;
	startRowNum: number;
	totalPages: number;
	endRowNum: number;
}

export type ProvisioningStep = {
  id: number;
  step: 'infrastructurePipeline' | 'dnsProvisioning' | 'frontendBuild';
  status: 'pending' | 'inProgress' | 'completed' | 'failed';
};

export type payloadGlobalPlayerLifetimeRule = {
  applyDate: string;
  periodMonths: number;
};

export type wlCreatorSliceState = {
  list: {
    data: ITableData | null,
    error: any,
    isFetching: boolean,
  },
  createOptions: {
    data: any,
    error: any,
    isFetching: boolean,
  },
  create: {
    data: any,
    error: any,
    isFetching: boolean,
  },
  filterCreator: {
    data: IFilterCreatorData | null,
    error: any,
    isFetching: boolean,
  },
  platformConfig: {
    data: any,
    error: any,
    isFetching: boolean,
  },
  updatePlatformConfig: {
    data: any,
    error: any,
    isFetching: boolean,
  },
  marketingConfig: {
    data: any,
    error: any,
    isFetching: boolean,
  },
  updateMarketing: {
    data: any,
    error: any,
    isFetching: boolean,
  },
  themes: {
    data: any,
    error: any,
    isFetching: boolean,
  },
  themeUpdate: {
    data: any,
    error: any,
    isFetching: boolean,
  },
  imagesUpload: {
    data: any,
    error: any,
    isFetching: boolean,
  },
  provisioningSteps: {
    data: ProvisioningStep[] | null,
    error: any,
    isFetching: boolean,
  },
  domainsConfig: {
    data: any,
    error: any,
    isFetching: boolean,
  },
  updateDomainsConfig: {
    data: any,
    error: any,
    isFetching: boolean,
  },
  setDefaultClickDomain: {
    data: any,
    error: any,
    isFetching: boolean,
  },
  globalPlayerLifetimeRules: {
    data: any,
    error: any,
    isFetching: boolean,
  },
  createGlobalPlayerLifetimeRule: {
    data: any,
    error: any,
    isFetching: boolean,
  },
  updateGlobalPlayerLifetimeRule: {
    data: any,
    error: any,
    isFetching: boolean,
  },
  deleteGlobalPlayerLifetimeRule: {
    data: any,
    error: any,
    isFetching: boolean,
  },
};

const initialState: wlCreatorSliceState = {
  list: {
    data: null,
    error: null,
    isFetching: false,
  },
  createOptions: {
    data: null,
    error: null,
    isFetching: false,
  },
  create: {
    data: null,
    error: null,
    isFetching: false,
  },
  filterCreator: {
    data: null,
    error: null,
    isFetching: false,
  },
  platformConfig: {
    data: null,
    error: null,
    isFetching: false,
  },
  updatePlatformConfig: {
    data: null,
    error: null,
    isFetching: false,
  },
  marketingConfig: {
    data: null,
    error: null,
    isFetching: false,
  },
  updateMarketing: {
    data: null,
    error: null,
    isFetching: false,
  },
  themes: {
    data: null,
    error: null,
    isFetching: false,
  },
  themeUpdate: {
    data: null,
    error: null,
    isFetching: false,
  },
  imagesUpload: {
    data: null,
    error: null,
    isFetching: false,
  },
  provisioningSteps: {
    data: null,
    error: null,
    isFetching: false,
  },
  domainsConfig: {
    data: null,
    error: null,
    isFetching: false,
  },
  updateDomainsConfig: {
    data: null,
    error: null,
    isFetching: false,
  },
  setDefaultClickDomain: {
    data: null,
    error: null,
    isFetching: false,
  },
  globalPlayerLifetimeRules: {
    data: null,
    error: null,
    isFetching: false,
  },
  createGlobalPlayerLifetimeRule: {
    data: null,
    error: null,
    isFetching: false,
  },
  updateGlobalPlayerLifetimeRule: {
    data: null,
    error: null,
    isFetching: false,
  },
  deleteGlobalPlayerLifetimeRule: {
    data: null,
    error: null,
    isFetching: false,
  },
};

export const getListData: any = createAsyncThunk(
  'admin/flatFee/geListData',
  async (payload: payloadList, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await wlCreatorService.getList(payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        return rejectWithValue(data)
      }

      dispatch(setGeneralProgressHide());

      return data;
    } catch (error) {
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error)
    }
  }
);

export const getFilterCreatorData: any = createAsyncThunk(
  "admin/wlCreator/getFilterCreatorData",
  async (payload: payloadFilterCreator, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());

    try {
      const response = await wlCreatorService.getFilterCreator(payload);
      const data = await response.json();

      if (data.hasOwnProperty("authorized") && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem("user");
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        return rejectWithValue(data);
      }

      dispatch(setGeneralProgressHide());

      return {
        payload: {
          list: data.list,
          totalPages: data.totalPages,
        },
      };
    } catch (error) {
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error);
    }
  }
);

export const getThemesData: any = createAsyncThunk(
  'admin/wlCreator/getThemesData',
  async (platformId: string | number, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await wlCreatorService.getThemes(platformId);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        return rejectWithValue(data)
      }

      dispatch(setGeneralProgressHide());

      return data;
    } catch (error) {
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error)
    }
  }
);

export const updateThemeData: any = createAsyncThunk(
  'admin/wlCreator/updateThemeData',
  async (payload: { platformId: string | number; data: any}, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await wlCreatorService.updateTheme(payload.platformId, payload.data);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        dispatch(
          setGeneralSnackbarState({
            open: true,
            type: 'warning',
            message: 'error',
            messageKey: data?.error || 'common.messages.error',
          })
        );
        return rejectWithValue(data)
      }

      dispatch(setGeneralProgressHide());

      return data;
    } catch (error) {
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error)
    }
  }
);

export const uploadImagesData: any = createAsyncThunk(
  'admin/wlCreator/uploadImagesData',
  async (payload: { platformId: string | number; data: any}, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await wlCreatorService.uploadImages(payload.platformId, payload.data);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        dispatch(
          setGeneralSnackbarState({
            open: true,
            type: 'warning',
            message: 'error',
            messageKey: data?.error || 'common.messages.error',
          })
        );
        return rejectWithValue(data)
      }

      dispatch(setGeneralProgressHide());

      return data;
    } catch (error) {
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error)
    }
  }
);

export const getCreateOptionsData: any = createAsyncThunk(
  'admin/wlCreator/getCreateOptions',
  async (_, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await wlCreatorService.getCreateOptions();
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        return rejectWithValue(data)
      }

      dispatch(setGeneralProgressHide());

      return data;
    } catch (error) {
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error)
    }
  }
);

export const createPlatformData: any = createAsyncThunk(
  'admin/wlCreator/create',
  async (payload: any, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await wlCreatorService.create(payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        dispatch(
          setGeneralSnackbarState({
            open: true,
            type: 'warning',
            message: 'error',
            messageKey: 'common.messages.error',
          })
        );
        return rejectWithValue(data)
      }

      if (response.ok) {
        dispatch(
          setGeneralSnackbarState({
            open: true,
            type: 'success',
            message: 'success',
            messageKey: 'common.messages.wlCreated',
          })
        );
      }

      dispatch(setGeneralProgressHide());

      return data;
    } catch (error) {
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error)
    }
  }
);

export const getMarketingConfigData: any = createAsyncThunk(
  'admin/wlCreator/getMarketingConfig',
  async (platformId: string, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await wlCreatorService.getMarketingConfig(platformId);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        return rejectWithValue(data);
      }

      dispatch(setGeneralProgressHide());

      return data;
    } catch (error) {
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error);
    }
  }
);

export const updateMarketingConfigData: any = createAsyncThunk(
  'admin/wlCreator/updateMarketingConfig',
  async ({ platformId, payload }: { platformId: string; payload: any }, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await wlCreatorService.updateMarketingConfig(platformId, payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        dispatch(
          setGeneralSnackbarState({
            open: true,
            type: 'warning',
            message: 'error',
            messageKey: data?.error || 'common.messages.error',
          })
        );
        return rejectWithValue(data);
      }

      dispatch(
        setGeneralSnackbarState({
          open: true,
          type: 'success',
          message: 'success',
          messageKey: 'common.messages.success',
        })
      );

      dispatch(setGeneralProgressHide());

      return data;
    } catch (error) {
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error);
    }
  }
);

export const getPlatformConfigData: any = createAsyncThunk(
  'admin/wlCreator/getPlatformConfig',
  async (platformId: string, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await wlCreatorService.getPlatformConfig(platformId);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        return rejectWithValue(data);
      }

      dispatch(setGeneralProgressHide());
      return data;
    } catch (error) {
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error);
    }
  }
);

export const updatePlatformConfigData: any = createAsyncThunk(
  'admin/wlCreator/updatePlatformConfig',
  async ({ platformId, payload }: { platformId: string; payload: any }, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await wlCreatorService.updatePlatformConfig(platformId, payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        dispatch(
          setGeneralSnackbarState({
            open: true,
            type: 'warning',
            message: 'error',
            messageKey: data?.error || 'common.messages.error',
          })
        );
        return rejectWithValue(data);
      }

      dispatch(
        setGeneralSnackbarState({
          open: true,
          type: 'success',
          message: 'success',
          messageKey: 'common.messages.success',
        })
      );

      dispatch(setGeneralProgressHide());
      return data;
    } catch (error) {
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error);
    }
  }
);

export const getProvisioningStepsData: any = createAsyncThunk(
  'admin/wlCreator/getProvisioningSteps',
  async (platformId: string | number, { rejectWithValue }) => {
    try {
      const response = await wlCreatorService.getProvisioningSteps(platformId);
      const data = await response.json();

      if (response.status === 404 && data?.error === 'common.errors.platform.provisioning.not_found') {
        return null;
      }

      if (!response.ok) {
        return rejectWithValue(data);
      }

      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const getDomainsConfigData: any = createAsyncThunk(
  'admin/wlCreator/getDomainsConfigData',
  async (platformId: string, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await wlCreatorService.getDomainsConfig(platformId);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        return rejectWithValue(data);
      }

      dispatch(setGeneralProgressHide());

      return data;
    } catch (error) {
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error);
    }
  }
);

export const getGlobalPlayerLifetimeRulesData: any = createAsyncThunk(
  'admin/wlCreator/getGlobalPlayerLifetimeRulesData',
  async (payload: any, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await wlCreatorService.getGlobalPlayerLifetimeRules(payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        return rejectWithValue(data);
      }

      dispatch(setGeneralProgressHide());

      return data;
    } catch (error) {
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error);
    }
  }
);

export const createGlobalPlayerLifetimeRuleData: any = createAsyncThunk(
  'admin/wlCreator/createGlobalPlayerLifetimeRuleData',
  async ({ platformId, payload }: { platformId: string; payload: payloadGlobalPlayerLifetimeRule }, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await wlCreatorService.createGlobalPlayerLifetimeRule(platformId, payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        dispatch(
          setGeneralSnackbarState({
            open: true,
            type: 'warning',
            message: 'error',
            messageKey: data?.error || 'common.messages.error',
          })
        );
        return rejectWithValue(data);
      }

      dispatch(
        setGeneralSnackbarState({
          open: true,
          type: 'success',
          message: 'success',
          messageKey: 'common.messages.success',
        })
      );

      dispatch(setGeneralProgressHide());

      return data;
    } catch (error) {
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error);
    }
  }
);

export const updateGlobalPlayerLifetimeRuleData: any = createAsyncThunk(
  'admin/wlCreator/updateGlobalPlayerLifetimeRuleData',
  async ({ platformId, id, payload }: { platformId: string; id: number | string; payload: payloadGlobalPlayerLifetimeRule }, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await wlCreatorService.updateGlobalPlayerLifetimeRule(platformId, id, payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        dispatch(
          setGeneralSnackbarState({
            open: true,
            type: 'warning',
            message: 'error',
            messageKey: data?.error || 'common.messages.error',
          })
        );
        return rejectWithValue(data);
      }

      dispatch(
        setGeneralSnackbarState({
          open: true,
          type: 'success',
          message: 'success',
          messageKey: 'admin.wlCreator.costOptimization.rule_form.success_updated',
        })
      );

      dispatch(setGeneralProgressHide());

      return data;
    } catch (error) {
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error);
    }
  }
);

export const deleteGlobalPlayerLifetimeRuleData: any = createAsyncThunk(
  'admin/wlCreator/deleteGlobalPlayerLifetimeRuleData',
  async ({ platformId, id }: { platformId: string; id: number | string }, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await wlCreatorService.deleteGlobalPlayerLifetimeRule(platformId, id);

      const text = await response.text();
      const data = text ? JSON.parse(text) : {};

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        dispatch(
          setGeneralSnackbarState({
            open: true,
            type: 'warning',
            message: 'error',
            messageKey: data?.error || 'common.messages.error',
          })
        );
        return rejectWithValue(data);
      }

      dispatch(
        setGeneralSnackbarState({
          open: true,
          type: 'success',
          message: 'success',
          messageKey: 'common.messages.success',
        })
      );

      dispatch(setGeneralProgressHide());

      return data;
    } catch (error) {
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error);
    }
  }
);

export const updateDomainsConfigData: any = createAsyncThunk(
  'admin/wlCreator/updateDomainsConfigData',
  async ({ platformId, payload }: { platformId: string; payload: any }, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await wlCreatorService.updateDomainsConfig(platformId, payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        dispatch(
          setGeneralSnackbarState({
            open: true,
            type: 'warning',
            message: 'error',
            messageKey: data?.error || 'common.messages.error',
          })
        );
        return rejectWithValue(data);
      }

      dispatch(
        setGeneralSnackbarState({
          open: true,
          type: 'success',
          message: 'success',
          messageKey: 'common.messages.success',
        })
      );

      dispatch(setGeneralProgressHide());

      return data;
    } catch (error) {
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error);
    }
  }
);

export const setDefaultClickDomainData: any = createAsyncThunk(
  'admin/wlCreator/setDefaultClickDomain',
  async (payload: any, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await wlCreatorService.setDefaultClickDomain(payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        dispatch(
          setGeneralSnackbarState({
            open: true,
            type: 'warning',
            message: 'error',
            messageKey: data?.error || 'common.messages.error',
          })
        );
        return rejectWithValue(data);
      }

      dispatch(
        setGeneralSnackbarState({
          open: true,
          type: 'success',
          message: 'success',
          messageKey: 'common.messages.success',
        })
      );

      dispatch(setGeneralProgressHide());

      return data;
    } catch (error) {
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error);
    }
  }
);

export const wlCreatorSlice = createSlice({
  name: 'wlCreator',
  initialState: initialState,
  reducers: {
    clearListState(state) {
      state.list.error = null;
      state.list.isFetching = false;
      state.list.data = null;
    },
    clearFilterCreatorState(state) {
      state.filterCreator.error = null;
      state.filterCreator.isFetching = false;
      state.filterCreator.data = null;
    },
    clearThemesState(state) {
      state.themes.error = null;
      state.themes.isFetching = false;
      state.themes.data = null;
    },
    clearThemeUpdateState(state) {
      state.themeUpdate.error = null;
      state.themeUpdate.isFetching = false;
      state.themeUpdate.data = null;
    },
    clearImagesUploadState(state) {
      state.imagesUpload.error = null;
      state.imagesUpload.isFetching = false;
      state.imagesUpload.data = null;
    },
    clearCreateOptionsState(state) {
      state.createOptions.error = null;
      state.createOptions.isFetching = false;
      state.createOptions.data = null;
    },
    clearCreateState(state) {
      state.create.error = null;
      state.create.isFetching = false;
      state.create.data = null;
    },
    clearPlatformConfigState(state) {
      state.platformConfig.error = null;
      state.platformConfig.isFetching = false;
      state.platformConfig.data = null;
    },
    clearUpdatePlatformConfigState(state) {
      state.updatePlatformConfig.error = null;
      state.updatePlatformConfig.isFetching = false;
      state.updatePlatformConfig.data = null;
    },
    clearMarketingConfigState(state) {
      state.marketingConfig.error = null;
      state.marketingConfig.isFetching = false;
      state.marketingConfig.data = null;
    },
    clearUpdateMarketingState(state) {
      state.updateMarketing.error = null;
      state.updateMarketing.isFetching = false;
      state.updateMarketing.data = null;
    },
    clearProvisioningStepsState(state) {
      state.provisioningSteps.error = null;
      state.provisioningSteps.isFetching = false;
      state.provisioningSteps.data = null;
    },
    clearDomainsConfigState(state) {
      state.domainsConfig.error = null;
      state.domainsConfig.isFetching = false;
      state.domainsConfig.data = null;
    },
    clearUpdateDomainsConfigState(state) {
      state.updateDomainsConfig.error = null;
      state.updateDomainsConfig.isFetching = false;
      state.updateDomainsConfig.data = null;
    },
    clearSetDefaultClickDomainState(state) {
      state.setDefaultClickDomain.error = null;
      state.setDefaultClickDomain.isFetching = false;
      state.setDefaultClickDomain.data = null;
    },
    clearGlobalPlayerLifetimeRulesState(state) {
      state.globalPlayerLifetimeRules.error = null;
      state.globalPlayerLifetimeRules.isFetching = false;
      state.globalPlayerLifetimeRules.data = null;
    },
    clearCreateGlobalPlayerLifetimeRuleState(state) {
      state.createGlobalPlayerLifetimeRule.error = null;
      state.createGlobalPlayerLifetimeRule.isFetching = false;
      state.createGlobalPlayerLifetimeRule.data = null;
    },
    clearUpdateGlobalPlayerLifetimeRuleState(state) {
      state.updateGlobalPlayerLifetimeRule.error = null;
      state.updateGlobalPlayerLifetimeRule.isFetching = false;
      state.updateGlobalPlayerLifetimeRule.data = null;
    },
    clearDeleteGlobalPlayerLifetimeRuleState(state) {
      state.deleteGlobalPlayerLifetimeRule.error = null;
      state.deleteGlobalPlayerLifetimeRule.isFetching = false;
      state.deleteGlobalPlayerLifetimeRule.data = null;
    },
  },
  extraReducers: (builder: ActionReducerMapBuilder<NoInfer<wlCreatorSliceState>>) => {
    builder.addCase(getListData.pending, (state ) => {
      state.list.isFetching = true;
      state.list.error = null;
    });
    builder.addCase(getListData.fulfilled, (state , action) => {
      state.list.error = null;
      state.list.isFetching = false;
	    state.list.data = action.payload;
    });
    builder.addCase(getListData.rejected, (state , action) => {
      state.list.isFetching = false;
      state.list.error = action.payload;
      state.list.data = null;
    });

    builder.addCase(getFilterCreatorData.pending, (state ) => {
      state.filterCreator.isFetching = true;
      state.filterCreator.error = null;
    });
    builder.addCase(getFilterCreatorData.fulfilled, (state , action) => {
      state.filterCreator.error = null;
      state.filterCreator.isFetching = false;
	    state.filterCreator.data = action.payload;
    });
    builder.addCase(getFilterCreatorData.rejected, (state , action) => {
      state.filterCreator.isFetching = false;
      state.filterCreator.error = action.payload;
      state.filterCreator.data = null;
    });

    builder.addCase(getThemesData.pending, (state ) => {
      state.themes.isFetching = true;
      state.themes.error = null;
    });
    builder.addCase(getThemesData.fulfilled, (state , action) => {
      state.themes.error = null;
      state.themes.isFetching = false;
	    state.themes.data = action.payload;
    });
    builder.addCase(getThemesData.rejected, (state , action) => {
      state.themes.isFetching = false;
      state.themes.error = action.payload;
      state.themes.data = null;
    });

    builder.addCase(updateThemeData.pending, (state ) => {
      state.themeUpdate.isFetching = true;
      state.themeUpdate.error = null;
    });
    builder.addCase(updateThemeData.fulfilled, (state , action) => {
      state.themeUpdate.error = null;
      state.themeUpdate.isFetching = false;
	    state.themeUpdate.data = action.payload;
    });
    builder.addCase(updateThemeData.rejected, (state , action) => {
      state.themeUpdate.isFetching = false;
      state.themeUpdate.error = action.payload;
      state.themeUpdate.data = null;
    });

    builder.addCase(uploadImagesData.pending, (state ) => {
      state.imagesUpload.isFetching = true;
      state.imagesUpload.error = null;
    });
    builder.addCase(uploadImagesData.fulfilled, (state , action) => {
      state.imagesUpload.error = null;
      state.imagesUpload.isFetching = false;
	    state.imagesUpload.data = action.payload;
    });
    builder.addCase(uploadImagesData.rejected, (state , action) => {
      state.imagesUpload.isFetching = false;
      state.imagesUpload.error = action.payload;
      state.imagesUpload.data = null;
    });

    builder.addCase(getCreateOptionsData.pending, (state) => {
      state.createOptions.isFetching = true;
      state.createOptions.error = null;
    });
    builder.addCase(getCreateOptionsData.fulfilled, (state, action) => {
      state.createOptions.error = null;
      state.createOptions.isFetching = false;
      state.createOptions.data = action.payload;
    });
    builder.addCase(getCreateOptionsData.rejected, (state, action) => {
      state.createOptions.isFetching = false;
      state.createOptions.error = action.payload;
      state.createOptions.data = null;
    });

    builder.addCase(createPlatformData.pending, (state) => {
      state.create.isFetching = true;
      state.create.error = null;
    });
    builder.addCase(createPlatformData.fulfilled, (state, action) => {
      state.create.error = null;
      state.create.isFetching = false;
      state.create.data = action.payload;
    });
    builder.addCase(createPlatformData.rejected, (state, action) => {
      state.create.isFetching = false;
      state.create.error = action.payload;
      state.create.data = null;
    });

    builder.addCase(getPlatformConfigData.pending, (state) => {
      state.platformConfig.isFetching = true;
      state.platformConfig.error = null;
    });
    builder.addCase(getPlatformConfigData.fulfilled, (state, action) => {
      state.platformConfig.error = null;
      state.platformConfig.isFetching = false;
      state.platformConfig.data = action.payload;
    });
    builder.addCase(getPlatformConfigData.rejected, (state, action) => {
      state.platformConfig.isFetching = false;
      state.platformConfig.error = action.payload;
      state.platformConfig.data = null;
    });

    builder.addCase(updatePlatformConfigData.pending, (state) => {
      state.updatePlatformConfig.isFetching = true;
      state.updatePlatformConfig.error = null;
    });
    builder.addCase(updatePlatformConfigData.fulfilled, (state, action) => {
      state.updatePlatformConfig.error = null;
      state.updatePlatformConfig.isFetching = false;
      state.updatePlatformConfig.data = action.payload;
    });
    builder.addCase(updatePlatformConfigData.rejected, (state, action) => {
      state.updatePlatformConfig.isFetching = false;
      state.updatePlatformConfig.error = action.payload;
      state.updatePlatformConfig.data = null;
    });

    builder.addCase(getMarketingConfigData.pending, (state) => {
      state.marketingConfig.isFetching = true;
      state.marketingConfig.error = null;
    });
    builder.addCase(getMarketingConfigData.fulfilled, (state, action) => {
      state.marketingConfig.error = null;
      state.marketingConfig.isFetching = false;
      state.marketingConfig.data = action.payload;
    });
    builder.addCase(getMarketingConfigData.rejected, (state, action) => {
      state.marketingConfig.isFetching = false;
      state.marketingConfig.error = action.payload;
      state.marketingConfig.data = null;
    });

    builder.addCase(updateMarketingConfigData.pending, (state) => {
      state.updateMarketing.isFetching = true;
      state.updateMarketing.error = null;
    });
    builder.addCase(updateMarketingConfigData.fulfilled, (state, action) => {
      state.updateMarketing.error = null;
      state.updateMarketing.isFetching = false;
      state.updateMarketing.data = action.payload;
    });
    builder.addCase(updateMarketingConfigData.rejected, (state, action) => {
      state.updateMarketing.isFetching = false;
      state.updateMarketing.error = action.payload;
      state.updateMarketing.data = null;
    });

    builder.addCase(getProvisioningStepsData.pending, (state) => {
      state.provisioningSteps.isFetching = true;
      state.provisioningSteps.error = null;
    });
    builder.addCase(getProvisioningStepsData.fulfilled, (state, action: any) => {
      state.provisioningSteps.isFetching = false;
      state.provisioningSteps.error = null;
      state.provisioningSteps.data = action.payload;
    });
    builder.addCase(getProvisioningStepsData.rejected, (state, action) => {
      state.provisioningSteps.isFetching = false;
      state.provisioningSteps.error = action.payload;
    });

    builder.addCase(getDomainsConfigData.pending, (state) => {
      state.domainsConfig.isFetching = true;
      state.domainsConfig.error = null;
    });
    builder.addCase(getDomainsConfigData.fulfilled, (state, action) => {
      state.domainsConfig.error = null;
      state.domainsConfig.isFetching = false;
      state.domainsConfig.data = action.payload;
    });
    builder.addCase(getDomainsConfigData.rejected, (state, action) => {
      state.domainsConfig.isFetching = false;
      state.domainsConfig.error = action.payload;
      state.domainsConfig.data = null;
    });

    builder.addCase(updateDomainsConfigData.pending, (state) => {
      state.updateDomainsConfig.isFetching = true;
      state.updateDomainsConfig.error = null;
    });
    builder.addCase(updateDomainsConfigData.fulfilled, (state, action) => {
      state.updateDomainsConfig.error = null;
      state.updateDomainsConfig.isFetching = false;
      state.updateDomainsConfig.data = action.payload;
    });
    builder.addCase(updateDomainsConfigData.rejected, (state, action) => {
      state.updateDomainsConfig.isFetching = false;
      state.updateDomainsConfig.error = action.payload;
      state.updateDomainsConfig.data = null;
    });

    builder.addCase(setDefaultClickDomainData.pending, (state) => {
      state.setDefaultClickDomain.isFetching = true;
      state.setDefaultClickDomain.error = null;
    });
    builder.addCase(setDefaultClickDomainData.fulfilled, (state, action) => {
      state.setDefaultClickDomain.error = null;
      state.setDefaultClickDomain.isFetching = false;
      state.setDefaultClickDomain.data = action.payload;
    });
    builder.addCase(setDefaultClickDomainData.rejected, (state, action) => {
      state.setDefaultClickDomain.isFetching = false;
      state.setDefaultClickDomain.error = action.payload;
      state.setDefaultClickDomain.data = null;
    });

    builder.addCase(getGlobalPlayerLifetimeRulesData.pending, (state) => {
      state.globalPlayerLifetimeRules.isFetching = true;
      state.globalPlayerLifetimeRules.error = null;
    });
    builder.addCase(getGlobalPlayerLifetimeRulesData.fulfilled, (state, action) => {
      state.globalPlayerLifetimeRules.error = null;
      state.globalPlayerLifetimeRules.isFetching = false;
      state.globalPlayerLifetimeRules.data = action.payload;
    });
    builder.addCase(getGlobalPlayerLifetimeRulesData.rejected, (state, action) => {
      state.globalPlayerLifetimeRules.isFetching = false;
      state.globalPlayerLifetimeRules.error = action.payload;
      state.globalPlayerLifetimeRules.data = null;
    });

    builder.addCase(createGlobalPlayerLifetimeRuleData.pending, (state) => {
      state.createGlobalPlayerLifetimeRule.isFetching = true;
      state.createGlobalPlayerLifetimeRule.error = null;
    });
    builder.addCase(createGlobalPlayerLifetimeRuleData.fulfilled, (state, action) => {
      state.createGlobalPlayerLifetimeRule.error = null;
      state.createGlobalPlayerLifetimeRule.isFetching = false;
      state.createGlobalPlayerLifetimeRule.data = action.payload;
    });
    builder.addCase(createGlobalPlayerLifetimeRuleData.rejected, (state, action) => {
      state.createGlobalPlayerLifetimeRule.isFetching = false;
      state.createGlobalPlayerLifetimeRule.error = action.payload;
      state.createGlobalPlayerLifetimeRule.data = null;
    });

    builder.addCase(updateGlobalPlayerLifetimeRuleData.pending, (state) => {
      state.updateGlobalPlayerLifetimeRule.isFetching = true;
      state.updateGlobalPlayerLifetimeRule.error = null;
    });
    builder.addCase(updateGlobalPlayerLifetimeRuleData.fulfilled, (state, action) => {
      state.updateGlobalPlayerLifetimeRule.error = null;
      state.updateGlobalPlayerLifetimeRule.isFetching = false;
      state.updateGlobalPlayerLifetimeRule.data = action.payload;
    });
    builder.addCase(updateGlobalPlayerLifetimeRuleData.rejected, (state, action) => {
      state.updateGlobalPlayerLifetimeRule.isFetching = false;
      state.updateGlobalPlayerLifetimeRule.error = action.payload;
      state.updateGlobalPlayerLifetimeRule.data = null;
    });

    builder.addCase(deleteGlobalPlayerLifetimeRuleData.pending, (state) => {
      state.deleteGlobalPlayerLifetimeRule.isFetching = true;
      state.deleteGlobalPlayerLifetimeRule.error = null;
    });
    builder.addCase(deleteGlobalPlayerLifetimeRuleData.fulfilled, (state, action) => {
      state.deleteGlobalPlayerLifetimeRule.error = null;
      state.deleteGlobalPlayerLifetimeRule.isFetching = false;
      state.deleteGlobalPlayerLifetimeRule.data = action.payload;
    });
    builder.addCase(deleteGlobalPlayerLifetimeRuleData.rejected, (state, action) => {
      state.deleteGlobalPlayerLifetimeRule.isFetching = false;
      state.deleteGlobalPlayerLifetimeRule.error = action.payload;
      state.deleteGlobalPlayerLifetimeRule.data = null;
    });
  },
});
export const {
  clearListState,
  clearFilterCreatorState,
  clearThemesState,
  clearThemeUpdateState,
  clearImagesUploadState,
  clearCreateOptionsState,
  clearCreateState,
  clearPlatformConfigState,
  clearUpdatePlatformConfigState,
  clearMarketingConfigState,
  clearUpdateMarketingState,
  clearProvisioningStepsState,
  clearDomainsConfigState,
  clearUpdateDomainsConfigState,
  clearSetDefaultClickDomainState,
  clearGlobalPlayerLifetimeRulesState,
  clearCreateGlobalPlayerLifetimeRuleState,
  clearUpdateGlobalPlayerLifetimeRuleState,
  clearDeleteGlobalPlayerLifetimeRuleState,
} =wlCreatorSlice.actions;

export default wlCreatorSlice.reducer;
